import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowUpRight } from 'lucide-react';
import { useState } from 'react';
import Logo from './Logo';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServeOpen, setIsServeOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleScheduleCall = () => {
    window.open("https://calendly.com/kentfrazier/30-minutes", "_blank");
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
    }
    window.scrollTo(0, 0);
  };

  const handleNavClick = () => {
    setIsMenuOpen(false);
    setIsServeOpen(false);
    window.scrollTo(0, 0);
  };

  const serveChildren = [
    { to: '/services', label: 'For Leaders' },
    { to: '/for-organizations', label: 'For Organizations' },
  ];

  const navLinks: Array<
    | { type: 'link'; to: string; label: string }
    | { type: 'external'; href: string; label: React.ReactNode }
    | { type: 'group'; label: string; children: { to: string; label: string }[] }
  > = [
    { type: 'link', to: '/ai-transformation', label: 'AI Transformation' },
    { type: 'external', href: 'https://fullyhuman.ai', label: <>fullyhuman.<span className="text-primary">ai</span></> },
    { type: 'link', to: '/about', label: 'About' },
    { type: 'group', label: 'Who We Serve', children: serveChildren },
    { type: 'link', to: '/testimonials', label: 'Results' },
    { type: 'link', to: '/essays', label: 'Essays' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isServeActive = serveChildren.some((c) => c.to === location.pathname);

  return (
    <>
      <nav className="w-full bg-background fixed top-0 z-50 border-b border-border/50">
        <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12">
          {/* Main Nav Row */}
          <div className="flex justify-between items-center h-20 sm:h-24">
            {/* Logo */}
            <Link
              to="/"
              className="transition-all hover:opacity-80 flex-shrink-0"
              onClick={handleLogoClick}
              aria-label="ParadoxEdge home"
            >
              <Logo variant="primary" height={64} />
            </Link>

            

            <button 
              className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-foreground" />
              ) : (
                <Menu className="w-6 h-6 text-foreground" />
              )}
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {navLinks.map((link) => {
                if (link.type === 'link') {
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={handleNavClick}
                      className={`text-sm font-medium transition-colors relative group ${
                        isActive(link.to)
                          ? 'text-primary'
                          : 'text-foreground/90 hover:text-foreground'
                      }`}
                    >
                      {link.label}
                      <span
                        className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${
                          isActive(link.to) ? 'w-full' : 'w-0 group-hover:w-full'
                        }`}
                      />
                    </Link>
                  );
                }
                if (link.type === 'external') {
                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium transition-colors relative group inline-flex items-center gap-1 text-foreground/90 hover:text-foreground"
                    >
                      {link.label}
                      <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                      <span className="absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 w-0 group-hover:w-full" />
                    </a>
                  );
                }
                return (
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => setIsServeOpen(true)}
                    onMouseLeave={() => setIsServeOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => setIsServeOpen((v) => !v)}
                      className={`text-sm font-medium transition-colors relative group inline-flex items-center gap-1 ${
                        isServeActive
                          ? 'text-primary'
                          : 'text-foreground/90 hover:text-foreground'
                      }`}
                      aria-haspopup="true"
                      aria-expanded={isServeOpen}
                    >
                      {link.label}
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                      <span
                        className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${
                          isServeActive ? 'w-full' : 'w-0 group-hover:w-full'
                        }`}
                      />
                    </button>
                    {isServeOpen && (
                      <div className="absolute left-0 top-full pt-3 min-w-[220px] z-50">
                        <div className="bg-background border border-border/60 shadow-lg rounded-sm py-2">
                          {link.children.map((child) => (
                            <Link
                              key={child.to}
                              to={child.to}
                              onClick={handleNavClick}
                              className={`block px-5 py-2.5 text-sm transition-colors ${
                                isActive(child.to)
                                  ? 'text-primary'
                                  : 'text-foreground/90 hover:text-foreground hover:bg-muted'
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              
              <button
                onClick={handleScheduleCall}
                className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 text-sm font-semibold rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:scale-105"
              >
                Begin a Conversation
              </button>
            </div>
          </div>
          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-6 border-t border-border animate-fade-in">
              <div className="flex flex-col space-y-4">
                {navLinks.map((link) => {
                  if (link.type === 'link') {
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={handleNavClick}
                        className={`text-lg py-2 transition-colors ${
                          isActive(link.to)
                            ? 'text-primary font-semibold'
                            : 'text-foreground/90 hover:text-foreground'
                        }`}
                      >
                        {link.label}
                      </Link>
                    );
                  }
                  if (link.type === 'external') {
                    return (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleNavClick}
                        className="text-lg py-2 transition-colors text-foreground/90 hover:text-foreground inline-flex items-center gap-1.5"
                      >
                        {link.label}
                        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                      </a>
                    );
                  }
                  return (
                    <div key={link.label} className="py-2">
                      <p className="text-xs uppercase tracking-[0.2em] text-foreground/50 font-semibold mb-3">
                        {link.label}
                      </p>
                      <div className="flex flex-col space-y-3 pl-3 border-l border-border">
                        {link.children.map((child) => (
                          <Link
                            key={child.to}
                            to={child.to}
                            onClick={handleNavClick}
                            className={`text-lg transition-colors ${
                              isActive(child.to)
                                ? 'text-primary font-semibold'
                                : 'text-foreground/90 hover:text-foreground'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
                
                <button
                  onClick={handleScheduleCall}
                  className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-4 text-base font-semibold rounded-full transition-all duration-300 w-full"
                >
                  Begin a Conversation
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
      {/* Spacer to offset fixed nav height */}
      <div className="h-20 sm:h-24" />
    </>
  );
};

export default Navigation;
